import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Empty } from 'antd';
import ChartCard from './ChartCard';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import styled, { keyframes } from 'styled-components';

const COLORS = {
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  purple: '#C084FC',
  amber: '#FBBF24',
  red: '#F87171',
  green: '#10B981',
  cyan: '#22D3EE',
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 24px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.1) 0%, rgba(192, 132, 252, 0.1) 100%);
  border-radius: 12px;
  border: 1px solid rgba(34, 211, 238, 0.2);
`;

const StatItem = styled.div`
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #22D3EE 0%, #C084FC 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${COLORS.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LegendContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 32px);
  gap: 6px;
  justify-content: center;
  margin-top: 24px;
  padding: 0 16px;
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  color: ${COLORS.textPrimary};
  font-size: 10px;
  padding: 0px;
  border-radius: 0px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(34, 211, 238, 0.15);
    transform: scale(1.15);
    padding: 4px;
    gap: 3px;
    border-radius: 4px;
    margin: -4px;
    z-index: 10;
    position: relative;
  }
`;

const LegendColor = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${props => props.color};
  box-shadow: 0 2px 8px ${props => props.color}40;
  transition: all 0.3s ease;
  
  ${LegendItem}:hover & {
    transform: scale(1.2);
    box-shadow: 0 4px 12px ${props => props.color}60;
  }
`;

const MerchantLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  border: 1.5px solid rgba(34, 211, 238, 0.3);
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  
  ${LegendItem}:hover & {
    transform: scale(1.15);
    border-color: rgba(34, 211, 238, 0.6);
    box-shadow: 0 3px 10px rgba(34, 211, 238, 0.3);
  }
`;

const LegendInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  text-align: center;
`;

const LegendCount = styled.span`
  color: ${COLORS.textSecondary};
  font-weight: 600;
  font-size: 10px;
`;

const LegendPercentage = styled.span`
  color: ${COLORS.accent};
  font-size: 10px;
  font-weight: 600;
`;

const CHART_COLORS = [
  COLORS.cyan,
  COLORS.purple,
  COLORS.amber,
  COLORS.green,
  COLORS.red,
  '#A78BFA',
  '#F472B6',
  '#60A5FA',
  '#34D399',
  '#FBBF24',
];

interface WalletAssignment {
  merchantId: string | null;
  merchantName: string;
  logoUrl: string | null;
  count: number;
  percentage: number;
}

interface WalletAssignmentsData {
  assignments: WalletAssignment[];
  totalWallets: number;
}

const WalletAssignmentsChart: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<WalletAssignmentsData | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WALLET_ASSIGNMENTS_BY_MERCHANT);
      if (response.data?.status === 'success' && response.data?.data) {
        setData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching wallet assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = data?.assignments.map((item, index) => ({
    name: item.merchantName,
    value: item.count,
    percentage: item.percentage,
    logoUrl: item.logoUrl,
    color: CHART_COLORS[index % CHART_COLORS.length],
  })) || [];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div style={{
          background: '#161B22',
          border: '1px solid #1f2a37',
          borderRadius: '8px',
          padding: '12px',
          color: COLORS.textPrimary,
        }}>
          <p style={{ margin: 0, marginBottom: '4px', fontWeight: 600 }}>
            {data.payload.name}
          </p>
          <p style={{ margin: 0, color: COLORS.textSecondary }}>
            Wallets: {data.value}
          </p>
          <p style={{ margin: 0, color: COLORS.textSecondary }}>
            Percentage: {data.payload.percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show label for slices < 5%

    return (
      <text
        x={x}
        y={y}
        fill={COLORS.textPrimary}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ChartCard title="Wallet Assignments by Merchant" loading={loading}>
      {!loading && (!data || chartData.length === 0) ? (
        <Empty description="No wallet assignment data available" />
      ) : (
        <>
          {data && (
            <StatsContainer>
              <StatItem>
                <StatValue>{data.totalWallets.toLocaleString()}</StatValue>
                <StatLabel>Total Wallets</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>{data.assignments.length}</StatValue>
                <StatLabel>Merchants</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>
                  {data.assignments.length > 0 
                    ? Math.max(...data.assignments.map(a => a.percentage)).toFixed(1)
                    : 0}%
                </StatValue>
                <StatLabel>Top Share</StatLabel>
              </StatItem>
            </StatsContainer>
          )}
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  {chartData.map((entry, index) => (
                    <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                      <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={CustomLabel}
                  outerRadius={140}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={800}
                  animationEasing="ease-out"
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`url(#gradient-${index})`}
                      stroke={entry.color}
                      strokeWidth={2}
                      style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }}
                    />
                  ))}
                </Pie>
                <Tooltip 
                  content={<CustomTooltip />}
                  cursor={{ fill: 'rgba(34, 211, 238, 0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
          <LegendContainer>
            {chartData.map((item, index) => (
              <LegendItem key={index} title={item.name}>
                {item.logoUrl && (
                  <MerchantLogo src={item.logoUrl} alt={item.name} />
                )}
                <LegendInfo>
                  <LegendColor color={item.color} />
                  <LegendCount>({item.value.toLocaleString()})</LegendCount>
                  <LegendPercentage>{item.percentage.toFixed(1)}%</LegendPercentage>
                </LegendInfo>
              </LegendItem>
            ))}
          </LegendContainer>
        </>
      )}
    </ChartCard>
  );
};

export default WalletAssignmentsChart;

